import { MediasLogo } from "~/features/navigation/components/TopBar/MediasLogo";
import { trackRegisterInAffiliate } from "~/app/analytics/ga/affiliate";
import { useTranslation } from "react-i18next";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useIsMinPhoneSize } from "~/common/hooks/useMediaQuery";
import { selectVisitedAffiliate } from "~/domains/affiliate/redux/selectors";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, styled, type SxProps, type Theme, Typography } from "~/common/mui";
import PersonIcon from "@mui/icons-material/Person";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";

interface Props {
  className?: string;
  sx?: SxProps<Theme>;
}

export interface AffiliateRegisterBannerViewProps extends Props {
  text: string;
  buttonLabel: string;
  isPhone: boolean;
  onRegister: () => void;
}

const RegisterContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  rowGap: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.surface.main,
  borderRadius: theme.spacing(0.5),
  [theme.breakpoints.up("desktop-s")]: {
    padding: `${theme.spacing(2.5)} ${theme.spacing(3.75)}`,
  },
}));

const RegisterLogo = styled(MediasLogo)(({ theme }) => ({
  width: 60,
  height: "auto",
  [theme.breakpoints.up("desktop-s")]: {
    width: 105,
  },
}));

const RegisterSeparator = styled(Box)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  height: 60,
  margin: `0 ${theme.spacing(1.5)}`,
}));

const RegisterText = styled(Typography)(({ theme }) => ({
  flex: 2,
  margin: `0 ${theme.spacing(1.25)}`,
}));

export const AffiliateRegisterBannerView = ({
  className,
  sx,
  text,
  buttonLabel,
  isPhone,
  onRegister,
}: AffiliateRegisterBannerViewProps) => {
  return (
    <RegisterContainer className={className} sx={sx}>
      <RegisterLogo variant={"plus"} />
      <RegisterSeparator />
      <RegisterText>{text}</RegisterText>
      <Button
        sx={{ width: "100%", minHeight: 30, mt: isPhone ? 0 : 3, ...(!isPhone && { width: "auto", mx: 2.5 }) }}
        onClick={onRegister}
        data-test-id={"banner-partner-register"}
        startIcon={<PersonIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
      >
        {buttonLabel}
      </Button>
    </RegisterContainer>
  );
};

export const AffiliateRegisterBanner = ({ className, sx }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const partner = useSelector(selectVisitedAffiliate);
  const isPhone = useIsMinPhoneSize();

  return (
    <>
      <AffiliateRegisterBannerView
        className={className}
        sx={sx}
        text={
          userHasPermission(Permission.ADD_TO_CART)
            ? t("affiliateSalesPartner.banner.new.text.register")
            : t("affiliateSalesPartner.banner.text.register")
        }
        buttonLabel={t("affiliateSalesPartner.banner.button.registerForFree")}
        isPhone={isPhone}
        onRegister={() => {
          trackRegisterInAffiliate(partner?.name ?? "");
          dispatch(openAuthOverlay("register"));
        }}
      />
    </>
  );
};
